import { ReleasePublicStatus } from '@cycle-app/graphql-codegen';
import { DotsMenuProps, SelectOption } from '@cycle-app/ui';
import {
  CancelCircleIcon, CheckCircleIcon, PenIcon, TrashIcon, EyeClosedIcon, CalendarEditIcon,
} from '@cycle-app/ui/icons';
import { useMemo } from 'react';

import { ReleaseRemoveModal } from 'src/components/ReleaseModals';
import { useReleasePublicStatusUpdate } from 'src/hooks/releases/useReleasePublicStatusUpdate';
import { useGetPermission } from 'src/reactives';
import { setLimitationsModal } from 'src/reactives/limitationsModal.reactive';
import { openUpdateRelease, openRemoveRelease } from 'src/reactives/releases.reactive';

import { StyledDotsMenu } from './ReleaseActionsMenu.styles';

type Props = Omit<DotsMenuProps, 'options'> & {
  publicStatus: ReleasePublicStatus;
  releaseId: string;
};

export const ReleaseActionsMenu = ({
  releaseId, publicStatus,
}: Props) => {
  const {
    editPublished, publish, unpublish, discardChanges,
  } = useReleasePublicStatusUpdate(releaseId);
  const {
    canUpdateRelease, canDeleteRelease,
  } = useGetPermission();
  const isPublished = publicStatus === ReleasePublicStatus.Published;

  const options = useMemo(() => {
    const result: SelectOption[] = [];

    if (publicStatus === ReleasePublicStatus.Unpublished) {
      result.push({
        icon: <CheckCircleIcon />,
        label: 'Publish release',
        value: 'publish',
        onSelect: publish,
      });
    }

    if (isPublished) {
      result.push({
        icon: <PenIcon />,
        label: 'Make updates',
        value: 'update',
        onSelect: editPublished,
      });
      result.push({
        icon: <EyeClosedIcon />,
        label: 'Unpublish',
        value: 'unpublish',
        onSelect: unpublish,
      });
    }

    if (publicStatus === ReleasePublicStatus.Editing) {
      result.push({
        icon: <CheckCircleIcon />,
        label: 'Publish changes',
        value: 'publish',
        onSelect: publish,
      });
      result.push({
        icon: <CancelCircleIcon />,
        label: 'Discard changes',
        value: 'discard',
        onSelect: discardChanges,
      });
      result.push({
        icon: <EyeClosedIcon />,
        label: 'Unpublish',
        value: 'unpublish',
        onSelect: unpublish,
      });
    }

    return [
      ...result,
      {
        disabled: isPublished,
        icon: <CalendarEditIcon width={14} style={{ marginLeft: '2px' }} />,
        label: 'Edit release info',
        value: 'edit-infos',
        onSelect: canUpdateRelease ? openUpdateRelease(releaseId) : () => setLimitationsModal({ action: 'RELEASE_UPDATE' }),
        ...isPublished && {
          tooltipContent: 'Make updates or unpublish first',
        },
      }, {
        disabled: isPublished,
        icon: <TrashIcon />,
        label: 'Delete',
        value: 'delete',
        onSelect: canDeleteRelease ? openRemoveRelease(releaseId) : () => setLimitationsModal({ action: 'RELEASE_UPDATE' }),
        ...isPublished && {
          tooltipContent: 'Unpublish first',
        },
      },
    ];
  }, [discardChanges, isPublished, canDeleteRelease, canUpdateRelease, editPublished, publicStatus, publish, releaseId, unpublish]);

  return (
    <>
      <StyledDotsMenu
        placement="right-start"
        options={options}
        width={160}
      />
      <ReleaseRemoveModal releaseId={releaseId} />
    </>
  );
};
